import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { GoogleAnalyticsEvents } from '@web/core/google-analytics/google-analytics.events';
import { AuthStatus, useAuthState } from '@packages/contexts/auth';
import { getLogger } from '@packages/core/logging';
import { ModuleName } from '@web/core/navigation/module-name';
import { resolvePageReferrer, resolvePageRoute } from './page-view-tracker.helpers';
import { buildPageUrl, selectPageChangeParams } from './google-analytics.url';
import { googleAnalyticsService } from '@web/services/singletons';

const logger = getLogger('web.gtm');

const DEFAULT_PAGE_TITLE = 'Subaru TechShare';

/**
 * WEB-ONLY. Fires a GA4 `page_view` on every SPA navigation.
 *
 * Waits for auth to resolve before firing the first page_view. The service queues events until it has booted (which
 * the first `setUser` / `resetUser` triggers) and flushes the identify payload ahead of that queue, so the very first
 * GA4 hit carries the correct `user_id` and user properties, keeping the session non-anonymous from event one.
 *
 * Mounted by `<GoogleAnalyticsIntegration />`, alongside the rest of the Google Analytics wiring.
 */
export function PageViewTracker() {
    const { status: authStatus } = useAuthState();
    const { pathname, search } = useLocation();
    const lastTrackedKeyRef = useRef<string | null>(null);
    const lastTrackedUrlRef = useRef<string | null>(null);
    const lastRawReferrerRef = useRef<string | null>(null);

    useEffect(() => {
        try {
            const isAuthResolved = authStatus === AuthStatus.Authenticated || authStatus === AuthStatus.Unauthenticated;
            if (!isAuthResolved) {
                return;
            }
            const pageView = buildPageView(pathname, search, authStatus);
            if (!pageView) {
                return;
            }

            if (pageView.dedupeKey === lastTrackedKeyRef.current && pathname === lastRawReferrerRef.current) {
                return;
            }

            const origin = window.location.origin;
            const pageLocation = buildPageUrl(origin, pageView.pagePath, search);
            const pageReferrer = resolvePageReferrer(lastTrackedUrlRef.current, document.referrer, origin);

            lastTrackedKeyRef.current = pageView.dedupeKey;
            lastTrackedUrlRef.current = pageLocation;
            lastRawReferrerRef.current = pathname;

            googleAnalyticsService.track(GoogleAnalyticsEvents.PageView, {
                page_location: pageLocation,
                page_referrer: pageReferrer,
                page_title: DEFAULT_PAGE_TITLE,
                module_name: pageView.moduleName,
            });
        } catch (error) {
            logger.error('Failed to track the page view', { error });
        }
    }, [authStatus, pathname, search]);

    return null;
}

type PageView = {
    pagePath: string;
    dedupeKey: string;
    moduleName: ModuleName;
};

/**
 * `dedupeKey` holds only the query keys that address a page, so filtering or paging a list does not fire a second
 * `page_view` identical to the first. Two pages sharing a pattern are told apart by the raw pathname the caller
 * compares alongside this key.
 */
function buildPageView(pathname: string, search: string, authStatus: AuthStatus): PageView | null {
    const { pattern, moduleName, isRedirectOnly } = resolvePageRoute(pathname);
    const isAuthenticated = authStatus === AuthStatus.Authenticated;

    if (isAuthenticated && isRedirectOnly) {
        return null;
    }

    // The root URL is two pages behind one address. The audience is in the key so the dashboard is still reported
    // when auth resolves, rather than suppressed as a repeat of the landing page.
    if (pattern === '/' && !isAuthenticated) {
        return {
            pagePath: pattern,
            moduleName: ModuleName.PublicLanding,
            dedupeKey: buildDedupeKey(pattern, search, 'public'),
        };
    }

    if (!moduleName) {
        return null;
    }

    return { pagePath: pattern, moduleName, dedupeKey: buildDedupeKey(pattern, search, 'app') };
}

function buildDedupeKey(pagePath: string, search: string, audience: 'public' | 'app'): string {
    return `${pagePath}::${selectPageChangeParams(search)}::${audience}`;
}
